<template>
  <v-card flat class="time-input-card">
    <v-card flat class="input-card pa-4">
      <v-chip-group v-model="mode" row mandatory class="timer-mode-chips mb-4">
        <v-chip value="stopwatch" outlined> ⏱ Stoppuhr </v-chip>
        <v-chip value="countdown" outlined> ⏳ Countdown </v-chip>
        <v-chip value="manual" outlined> 🔢 Manuell </v-chip>
      </v-chip-group>

      <div v-if="mode !== 'manual'" class="text-center timer-section">
        <div class="display-time-wrapper">
          <div class="display-time monospace" :class="{ running: running }">{{ formattedTime }}</div>
        </div>

        <v-text-field
          v-if="mode === 'countdown' && !running"
          v-model.number="countDownSeconds"
          label="Startzeit (Sekunden)"
          type="number"
          dense
          inputmode="numeric"
          outlined
          hide-details
          class="mb-6 countdown-input" />

        <div class="controls-wrapper">
          <v-btn fab small color="rgba(255, 255, 255, 0.1)" @click="resetTimer" class="control-btn">
            <v-icon>mdi-restart</v-icon>
          </v-btn>
          <v-btn fab large :color="running ? 'red' : 'green'" @click="toggleTimer" class="mx-4 play-pause-btn">
            <v-icon size="36">{{ running ? "mdi-pause" : "mdi-play" }}</v-icon>
          </v-btn>
          <v-btn fab small color="rgba(255, 255, 255, 0.1)" @click="submit" :disabled="totalSeconds === 0" class="control-btn">
            <v-icon>mdi-check</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-else>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model.number="manualMinutes" inputmode="numeric" type="number" label="Minuten" outlined dense hide-details />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model.number="manualSeconds" inputmode="numeric" type="number" label="Sekunden" outlined dense hide-details />
          </v-col>
        </v-row>
      </div>

      <v-btn v-if="mode === 'manual'" block large class="mt-4 add-set-btn" color="primary" :disabled="totalSeconds === 0" @click="submit">
        Satz hinzufügen
      </v-btn>
    </v-card>
  </v-card>
</template>

<script>
export default {
  name: "TimeInput",
  props: ["exercise", "planSettings"],
  data() {
    return {
      mode: "stopwatch",
      milliseconds: 0,
      timer: null,
      running: false,
      startTime: null,
      countDownSeconds: 0,
      manualMinutes: 0,
      manualSeconds: 0,
    }
  },
  computed: {
    totalSeconds() {
      if (this.mode === "manual") {
        return (this.manualMinutes || 0) * 60 + (this.manualSeconds || 0)
      }
      return this.milliseconds / 1000
    },
    formattedTime() {
      const totalSeconds = Math.floor(this.milliseconds / 1000)
      const min = String(Math.floor(totalSeconds / 60)).padStart(2, "0")
      const sec = String(totalSeconds % 60).padStart(2, "0")
      const hund = String(Math.floor((this.milliseconds % 1000) / 10)).padStart(2, "0")
      return `${min}:${sec}:${hund}`
    },
  },
  methods: {
    initDefaults() {
      const plannedDuration = this.planSettings?.find((s) => s.text === "Dauer")?.value
      if (plannedDuration) {
        this.countDownSeconds = Number(plannedDuration)
        this.milliseconds = this.countDownSeconds * 1000
      } else {
        this.countDownSeconds = 60 // Fallback
        this.milliseconds = 0
      }
    },
    toggleTimer() {
      if (this.running) {
        this.pauseTimer()
      } else {
        this.startTimer()
      }
    },
    startTimer() {
      if (this.running) return

      if (this.mode === "countdown" && this.milliseconds === 0) {
        this.milliseconds = this.countDownSeconds * 1000
      }

      this.running = true
      this.startTime = Date.now() - (this.mode === "stopwatch" ? this.milliseconds : 0)

      this.timer = setInterval(() => {
        if (this.mode === "stopwatch") {
          this.milliseconds = Date.now() - this.startTime
        } else {
          // Countdown
          const elapsed = Date.now() - this.startTime
          this.milliseconds = Math.max(0, this.countDownSeconds * 1000 - elapsed)
          if (this.milliseconds === 0) {
            this.pauseTimer()
            // Optional: Sound/Vibration abspielen
          }
        }
      }, 51) // Hohe Frequenz für flüssige Hundertstel
    },
    pauseTimer() {
      clearInterval(this.timer)
      this.running = false
    },
    resetTimer() {
      this.pauseTimer()
      this.milliseconds = this.mode === "countdown" ? this.countDownSeconds * 1000 : 0
    },
    submit() {
      this.pauseTimer()
      const value = this.totalSeconds
      if (value > 0) {
        // Wir speichern die Dauer in Sekunden für eine einfachere Weiterverarbeitung
        this.$emit("add-set", [{ text: "Dauer", value: value.toFixed(2) }])
        this.resetTimer()
        this.manualMinutes = 0
        this.manualSeconds = 0
      }
    },
  },
  watch: {
    mode(newVal) {
      this.resetTimer()
      if (newVal === "countdown") {
        this.milliseconds = this.countDownSeconds * 1000
      }
    },
    planSettings: {
      handler() {
        this.initDefaults()
      },
      immediate: true,
      deep: true,
    },
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
}
</script>

<style scoped>
.input-card {
  background-color: rgba(44, 44, 46, 0.7);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.timer-mode-chips .v-chip {
  font-size: 1rem;
}

.timer-section {
  padding: 16px 0;
}

.display-time-wrapper {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 24px;
  padding: 8px;
  margin-bottom: 24px;
}

.display-time {
  font-size: 3rem;
  font-weight: 700;
  color: #ffffff;
  background: transparent;
  padding: 16px;
  font-family: monospace;
  letter-spacing: 2px;
  transition: all 0.3s ease;
}

.display-time.running {
  color: var(--v-primary-base);
  text-shadow: 0 0 15px rgba(var(--v-primary-base-rgb), 0.7);
}

.controls-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.play-pause-btn {
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.4);
}

.countdown-input {
  max-width: 250px;
  margin-left: auto;
  margin-right: auto;
}

.add-set-btn {
  border-radius: 24px;
}
</style>
